import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,  TablePagination,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Edit as EditIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Settings as BulkEditIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchMappingsForBranch,
  updateMapping,
  importMappingsFromFile,
  exportMappings,
  clearImportResult,
  searchMappingsByCloudName,
  bulkUpdateMappings,
  clearBulkSearchResults,
} from '../store/slices/mappingSlice';
import { fetchAllBranches } from '../store/slices/branchSlice';
import { MenuItemMapping, Branch, CloudItemGrouping } from '../types';
import { formatDateTime } from '../utils/helpers';
import PermissionGate from '../components/Common/PermissionGate';
import { usePermission } from '../hooks/usePermissions';

interface MappingFormData {
  branchId: string;
  cloudMenuItemId: string;
  cloudMenuItemName: string;
  localMenuItemId: string;
  localMenuItemName: string;
  taxRate: number; // Changed from localPrice
  isActive: boolean;
  isModifier: boolean;
}

const initialFormData: MappingFormData = {
  branchId: '',
  cloudMenuItemId: '',
  cloudMenuItemName: '',
  localMenuItemId: '',
  localMenuItemName: '',
  taxRate: 0, // Changed from localPrice
  isActive: true,
  isModifier: false,
};

const MappingsPage: React.FC = () => {  const dispatch = useAppDispatch();
  const mappings = useAppSelector((state) => (state as any).mappings.mappings || []);
  const pagination = useAppSelector((state) => (state as any).mappings.pagination || null);
  const mappingsLoading = useAppSelector((state) => (state as any).mappings.loading || false);
  const mappingsError = useAppSelector((state) => (state as any).mappings.error || null);
  const bulkOperationLoading = useAppSelector((state) => (state as any).mappings.bulkOperationLoading || false);
  const importResult = useAppSelector((state) => (state as any).mappings.importResult || null);
  const exportLoading = useAppSelector((state) => (state as any).mappings.exportLoading || false);
  const branches = useAppSelector((state) => (state as any).branches.branches || []);
  
  // Bulk operations state
  const bulkSearchResults = useAppSelector((state) => (state as any).mappings.bulkSearchResults || null);
  const bulkSearchLoading = useAppSelector((state) => (state as any).mappings.bulkSearchLoading || false);
  const bulkUpdateLoading = useAppSelector((state) => (state as any).mappings.bulkUpdateLoading || false);  const [open, setOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<MenuItemMapping | null>(null);
  const [formData, setFormData] = useState<MappingFormData>(initialFormData);
  const [searchTerm, setSearchTerm] = useState('');const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Bulk operations state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [selectedGroupings, setSelectedGroupings] = useState<Set<string>>(new Set());
  const [bulkUpdateData, setBulkUpdateData] = useState({
    localMenuItemId: '',
    localMenuItemName: '',
    taxRate: '',
    isActive: true,
  });

  // Permission checks
  const canViewMappings = usePermission('mappings:read');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [selectedBranch, statusFilter, debouncedSearchTerm]);

  const currentQuery = useMemo(() => ({
    branchId: selectedBranch || undefined,
    searchTerm: debouncedSearchTerm || undefined,
    status: statusFilter || undefined,
    pageNumber: page + 1,
    pageSize: rowsPerPage,
  }), [selectedBranch, debouncedSearchTerm, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    if (canViewMappings) {
      dispatch(fetchMappingsForBranch(currentQuery));
      dispatch(fetchAllBranches());
    }
  }, [dispatch, canViewMappings, currentQuery]);

  const handleOpen = (mapping: MenuItemMapping) => {
    setEditingMapping(mapping);
    setFormData({
      branchId: mapping.branchId,
      cloudMenuItemId: mapping.cloudMenuItemId,
      cloudMenuItemName: mapping.cloudMenuItemName,
      localMenuItemId: mapping.localMenuItemId,
      localMenuItemName: mapping.localMenuItemName || '',
      taxRate: mapping.taxRate || 0,
      isActive: mapping.isActive,
      isModifier: mapping.isModifier,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingMapping(null);
    setFormData(initialFormData);
  };  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name!]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : type === 'number' 
          ? (name === 'taxRate' ? parseFloat(value as string) || 0 : parseFloat(value as string))
          : value,
    }));
  };
  const handleSubmit = async () => {
    try {
      const updatedMapping: MenuItemMapping = {
        ...editingMapping!,
        ...formData,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'User',
      };
      await dispatch(updateMapping(updatedMapping)).unwrap();
      handleClose();    } catch (error) {
      console.error('Error updating mapping:', error);
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Clear any previous import results
      dispatch(clearImportResult());
      
      const result = await dispatch(importMappingsFromFile({ 
        branchId: selectedBranch || undefined, 
        file 
      })).unwrap();

      // If successful, refresh the current query page
      if (result.success) {
        dispatch(fetchMappingsForBranch(currentQuery));
      }
    } catch (error) {
      console.error('Error importing file:', error);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      const result = await dispatch(exportMappings({ 
        branchId: selectedBranch || '', 
        format 
      })).unwrap();
      
      const mimeType = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8;';
      const extension = format === 'excel' ? 'xlsx' : 'csv';
      
      const blob = new Blob([result.data], { type: mimeType });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `menu_mappings_${new Date().toISOString().split('T')[0]}.${extension}`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error exporting mappings to ${format}:`, error);
    }
  };
  const handleExportCSV = () => handleExport('csv');
  const handleExportExcel = () => handleExport('excel');  const handleRefresh = async () => {
    console.log('Refreshing mappings with current filters');
    
    try {
      const result = await dispatch(fetchMappingsForBranch(currentQuery)).unwrap();
      console.log('Mappings refreshed successfully:', result?.totalCount || 0, 'matching mappings');
    } catch (error) {
      console.error('Error refreshing mappings:', error);
    }
  };

  const getBranchName = (branchId: string) => {
    const branch = branches.find((b: Branch) => b.id === branchId);
    return branch?.name || 'Unknown Branch';
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDismissImportResult = () => {
    dispatch(clearImportResult());
  };

  // Bulk operations handlers
  const handleBulkSearch = async () => {
    if (!bulkSearchTerm.trim()) return;
    
    try {
      await dispatch(searchMappingsByCloudName(bulkSearchTerm.trim())).unwrap();
      setSelectedGroupings(new Set()); // Clear selection when new search is performed
    } catch (error) {
      console.error('Error searching mappings:', error);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedGroupings.size === 0) return;

    try {      // Collect all mapping IDs from selected groupings
      const mappingIds: number[] = [];
      if (bulkSearchResults) {        bulkSearchResults.groupings.forEach((grouping: CloudItemGrouping) => {
          if (selectedGroupings.has(grouping.cloudMenuItemName)) {
            mappingIds.push(...grouping.mappings.map((m: MenuItemMapping) => m.id));
          }
        });
      }

      // Prepare update request
      const updates: {
        localMenuItemId?: string;
        localMenuItemName?: string;
        taxRate?: number;
        isActive?: boolean;
      } = {};
      if (bulkUpdateData.localMenuItemId.trim()) {
        updates.localMenuItemId = bulkUpdateData.localMenuItemId.trim();
      }
      if (bulkUpdateData.localMenuItemName.trim()) {
        updates.localMenuItemName = bulkUpdateData.localMenuItemName.trim();
      }      if (bulkUpdateData.taxRate.trim()) {
        const taxRateValue = parseFloat(bulkUpdateData.taxRate);
        if (!isNaN(taxRateValue)) {
          updates.taxRate = Math.round(taxRateValue * 100000) / 100000; // Preserve up to 5 decimal places
        }
      }
      updates.isActive = bulkUpdateData.isActive;

      await dispatch(bulkUpdateMappings({ mappingIds, updates })).unwrap();
      
      // Close dialog and refresh current branch mappings
      setBulkDialogOpen(false);
      dispatch(fetchMappingsForBranch(currentQuery));
      
      // Reset form
      setBulkSearchTerm('');
      setSelectedGroupings(new Set());
      setBulkUpdateData({
        localMenuItemId: '',
        localMenuItemName: '',
        taxRate: '',
        isActive: true,
      });
      dispatch(clearBulkSearchResults());
    } catch (error) {
      console.error('Error performing bulk update:', error);
    }
  };
  const handleToggleGroupingSelection = (cloudMenuItemName: string) => {
    const newSelection = new Set(selectedGroupings);
    if (newSelection.has(cloudMenuItemName)) {
      newSelection.delete(cloudMenuItemName);
    } else {
      newSelection.add(cloudMenuItemName);
    }
    setSelectedGroupings(newSelection);
  };
  const handleSelectAllGroupings = () => {
    if (!bulkSearchResults) return;
    const allGroupings = new Set<string>(bulkSearchResults.groupings.map((g: CloudItemGrouping) => g.cloudMenuItemName));
    setSelectedGroupings(allGroupings);
  };

  const handleDeselectAllGroupings = () => {
    setSelectedGroupings(new Set());
  };
  
  return (
    <PermissionGate permission="mappings:read" fallback={
      <Alert severity="error" sx={{ mt: 2 }}>
        You don't have permission to view mappings.
      </Alert>
    }>
      <Box sx={{ p: 2 }}> {/* MODIFIED HERE: Removed maxHeight and overflowY */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>        <Box display="flex" gap={1}>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleImportFile}
          />          <Tooltip title={selectedBranch ? `Refresh mappings for ${getBranchName(selectedBranch)}` : "Refresh all mappings"}>
            <Button
              variant="contained"
              color="primary"
              startIcon={mappingsLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={mappingsLoading}
              sx={{ 
                minWidth: '120px',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                }
              }}
            >
              {mappingsLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Tooltip>
          
          <PermissionGate
            permissions={['mappings:create', 'mappings:import', 'mappings:export']}
            fallback={
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={bulkOperationLoading}
              >
                {bulkOperationLoading ? 'Importing...' : 'Import File'}
              </Button>
            }
          >
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={bulkOperationLoading}
            >
              {bulkOperationLoading ? 'Importing...' : 'Import File'}
            </Button>          </PermissionGate>
          <PermissionGate permissions={['mappings:export']}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={exportLoading}
            >
              Export CSV
            </Button>
          </PermissionGate>          <PermissionGate permissions={['mappings:export']}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={exportLoading}
            >
              {exportLoading ? 'Exporting...' : 'Export Excel'}
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['mappings:manage']}>
            <Button
              variant="outlined"
              startIcon={<BulkEditIcon />}
              onClick={() => setBulkDialogOpen(true)}
              disabled={bulkSearchLoading || bulkUpdateLoading}
            >
              Bulk Operations
            </Button>
          </PermissionGate>
        </Box>
      </Box>{mappingsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mappingsError}
        </Alert>
      )}

      {/* Import Result Display */}
      {importResult && (
        <Alert 
          severity={importResult.success ? "success" : "error"} 
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismissImportResult}
            >
              ×
            </IconButton>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Import Results
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {importResult.message || (importResult.success ? 'Import completed successfully!' : 'Import failed!')}
          </Typography>
          {(importResult.importedCount > 0 || importResult.updatedCount > 0 || importResult.skippedCount > 0) && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              {importResult.importedCount > 0 && (
                <Chip size="small" color="success" label={`${importResult.importedCount} imported`} onClick={(e) => e.stopPropagation()} />
              )}
              {importResult.updatedCount > 0 && (
                <Chip size="small" color="info" label={`${importResult.updatedCount} updated`} onClick={(e) => e.stopPropagation()} />
              )}
              {importResult.skippedCount > 0 && (
                <Chip size="small" color="warning" label={`${importResult.skippedCount} skipped`} onClick={(e) => e.stopPropagation()} />
              )}
            </Box>
          )}
          {importResult.errors && importResult.errors.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Errors ({importResult.errors.length}):
              </Typography>              <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                {importResult.errors.slice(0, 10).map((error: string, index: number) => (
                  <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem', mb: 0.25 }}>
                    • {error}
                  </Typography>
                ))}
                {importResult.errors.length > 10 && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                    ... and {importResult.errors.length - 10} more errors
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Alert>
      )}

      {/* Filters */}      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <Box flex={1} minWidth="200px">
              <TextField
                fullWidth
                placeholder="Search mappings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>
            <Box minWidth="150px">
              <FormControl fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  label="Branch"
                  onChange={(e) => setSelectedBranch(e.target.value as string)}
                >                <MenuItem value="">All Branches</MenuItem>
                  {branches.map((branch: Branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box minWidth="150px">
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Showing {mappings.length} of {pagination?.totalCount || 0} matching mappings
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Mappings Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 'none' }}> {/* MODIFIED HERE */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination?.totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <Table>
          <TableHead>            <TableRow>
              <TableCell>Branch</TableCell>
              <TableCell>Talabat</TableCell>
              <TableCell>Micros </TableCell>
              <TableCell>Tax Rate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Sync</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappingsLoading && mappings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : mappings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">                  <Typography color="text.secondary">
                    {searchTerm || selectedBranch || statusFilter 
                      ? 'No mappings match your filters' 
                      : 'No mappings found. Sync branches from Talabat to create mappings.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              mappings.map((mapping: MenuItemMapping) => (
                <TableRow key={mapping.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {getBranchName(mapping.branchId)}
                    </Typography>
                  </TableCell>                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {mapping.cloudMenuItemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                         {mapping.cloudMenuItemId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {mapping.localMenuItemName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                       {mapping.localMenuItemId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">                     
                       {mapping.taxRate !== null && mapping.taxRate !== undefined 
                        ? `${parseFloat(mapping.taxRate.toFixed(5))}`
                        : 'N/A'
                      }
                    </Typography>
                  </TableCell>                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">                      <Chip
                        label={mapping.isActive ? 'Active' : 'Inactive'}
                        color={mapping.isActive ? 'success' : 'default'}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {mapping.isModifier && (                        <Chip
                          label="Modifier"
                          color="info"
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {mapping.lastUpdated ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Box>
                          <Typography variant="caption" display="block">
                            {formatDateTime(mapping.lastUpdated)}
                          </Typography>
                          {mapping.updatedBy && (
                            <Typography variant="caption" color="text.secondary">
                              by {mapping.updatedBy}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Never updated
                      </Typography>
                    )}
                  </TableCell>                  <TableCell align="right">
                    <PermissionGate permissions={["mappings:update", "mappings:manage"]}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpen(mapping)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>      {/* Edit Mapping Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Menu Item Mapping
        </DialogTitle><DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <Stack spacing={2}>              <FormControl fullWidth margin="dense">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={formData.branchId}
                  label="Branch"
                  disabled
                  required
                >
                  {branches.map((branch: Branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                <Box display="flex" gap={2}>
                <TextField
                  margin="dense"
                  label="Cloud Item ID"
                  fullWidth
                  variant="outlined"
                  value={formData.cloudMenuItemId}
                  InputProps={{ readOnly: true }}
                  required
                />
                <TextField
                  margin="dense"
                  label="Cloud Item Name"
                  fullWidth
                  variant="outlined"
                  value={formData.cloudMenuItemName}
                  InputProps={{ readOnly: true }}
                  required
                />
              </Box>
              
              <Box display="flex" gap={2}>
                <TextField
                  margin="dense"
                  label="Local Item ID"
                  fullWidth
                  variant="outlined"
                  value={formData.localMenuItemId}
                  onChange={(e) => setFormData({ ...formData, localMenuItemId: e.target.value })}
                  required
                />
                <TextField
                  margin="dense"
                  label="Local Item Name"
                  fullWidth
                  variant="outlined"
                  value={formData.localMenuItemName}
                  onChange={(e) => setFormData({ ...formData, localMenuItemName: e.target.value })}
                  required
                />
              </Box>              <TextField
                margin="dense"
                label="Tax Rate (%)"
                type="number"
                fullWidth
                variant="outlined"
                name="taxRate"
                value={formData.taxRate}
                onChange={handleChange}
                sx={{ mb: 2 }}
                inputProps={{
                  step: "0.00001",
                  min: "0",
                  max: "100"
                }}
                helperText="Enter tax rate percentage with up to 5 decimal places"
              />
              
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isModifier}
                      onChange={(e) => setFormData({ ...formData, isModifier: e.target.checked })}
                    />
                  }
                  label="Is Modifier"
                  sx={{ ml: 2 }}
                />
              </Box>
            </Stack>
          </Box>
        </DialogContent>        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Update
          </Button>        </DialogActions>
      </Dialog>
      
      {/* Bulk Operations Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Bulk Operations - Menu Item Mappings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Search Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Search by Cloud Item Name
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField
                    fullWidth
                    label="Cloud Item Name"
                    placeholder="Enter cloud menu item name to search..."
                    value={bulkSearchTerm}
                    onChange={(e) => setBulkSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleBulkSearch()}
                  />
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleBulkSearch}
                    disabled={!bulkSearchTerm.trim() || bulkSearchLoading}
                  >
                    {bulkSearchLoading ? 'Searching...' : 'Search'}
                  </Button>
                </Box>
                {bulkSearchLoading && (
                  <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Search Results */}
            {bulkSearchResults && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                      Search Results ({bulkSearchResults.totalMappings} mappings in {bulkSearchResults.totalGroups} groups)
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        onClick={handleSelectAllGroupings}
                        disabled={selectedGroupings.size === bulkSearchResults.groupings.length}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        onClick={handleDeselectAllGroupings}
                        disabled={selectedGroupings.size === 0}
                      >
                        Deselect All
                      </Button>
                    </Box>
                  </Box>
                  
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {bulkSearchResults.groupings.map((grouping: CloudItemGrouping) => (
                      <Accordion key={grouping.cloudMenuItemName}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" width="100%">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={selectedGroupings.has(grouping.cloudMenuItemName)}
                                  onChange={() => handleToggleGroupingSelection(grouping.cloudMenuItemName)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label=""
                              sx={{ mr: 2 }}
                            />
                            <Box flex={1}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {grouping.cloudMenuItemName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {grouping.totalCount} mappings across {Object.keys(grouping.branchCounts).length} branches
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>                          <Grid container spacing={2}>
                            {Object.entries(grouping.branchCounts).map(([branchId, count]) => (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={branchId}>
                                <Chip
                                  label={`${grouping.branchNames[branchId] || branchId}: ${count} items`}
                                  variant="outlined"
                                  size="small"
                                />
                              </Grid>
                            ))}
                          </Grid>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Sample Mappings:
                          </Typography>
                          {grouping.mappings.slice(0, 3).map((mapping: MenuItemMapping) => (
                            <Typography key={mapping.id} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                              • {mapping.localMenuItemName || mapping.localMenuItemId} 
                              ({getBranchName(mapping.branchId)})
                            </Typography>
                          ))}
                          {grouping.mappings.length > 3 && (
                            <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic' }}>
                              ... and {grouping.mappings.length - 3} more
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Bulk Update Form */}
            {selectedGroupings.size > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Bulk Update ({selectedGroupings.size} groups selected)
                  </Typography>                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Local Menu Item ID"
                        placeholder="Leave empty to keep current values"
                        value={bulkUpdateData.localMenuItemId}
                        onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, localMenuItemId: e.target.value })}
                      />
                    </Grid>                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Local Menu Item Name"
                        placeholder="Leave empty to keep current values"
                        value={bulkUpdateData.localMenuItemName}
                        onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, localMenuItemName: e.target.value })}
                      />
                    </Grid>                    <Grid size={{ xs: 12, sm: 6 }}>                      <TextField
                        fullWidth
                        label="Tax Rate (%)"
                        type="number"
                        placeholder="Leave empty to keep current values"
                        value={bulkUpdateData.taxRate}
                        onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, taxRate: e.target.value })}
                        inputProps={{
                          step: "0.00001",
                          min: "0",
                          max: "100"
                        }}
                        helperText="Enter tax rate percentage with up to 5 decimal places"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={bulkUpdateData.isActive}
                            onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, isActive: e.target.checked })}
                          />
                        }
                        label="Is Active"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          {selectedGroupings.size > 0 && (
            <Button
              onClick={handleBulkUpdate}
              variant="contained"
              disabled={bulkUpdateLoading}
            >
              {bulkUpdateLoading ? 'Updating...' : `Update ${selectedGroupings.size} Groups`}              
            </Button>
          )}
        </DialogActions>
      </Dialog>
      </Box>
    </PermissionGate>
  );
};

export default MappingsPage;
